import { useAtom, atom } from 'jotai';
import { Checkbox } from '@carbon/react';
import style from 'components/map/map.module.scss';
import { useState, useEffect } from 'react';

export const isCRMTractsVisibleAtom = atom(true);
export const isCRMSubtractsVisibleAtom = atom(true);
export const isTitleAssetsVisibleAtom = atom(true);
export const isWellsVisibleAtom = atom(true);

// This atom is used by the ScopeLayersSelection component to toggle all visible layers
// The component will pass the hidden layers as parameters
export const toggleAllScopeLayersAtom = atom(null, (get, set) => {
  // Get the current state of all layers
  const allVisible =
    get(isCRMTractsVisibleAtom) &&
    get(isCRMSubtractsVisibleAtom) &&
    get(isTitleAssetsVisibleAtom) &&
    get(isWellsVisibleAtom);

  // Toggle all layers to the opposite state
  set(isCRMTractsVisibleAtom, !allVisible);
  set(isCRMSubtractsVisibleAtom, !allVisible);
  set(isTitleAssetsVisibleAtom, !allVisible);
  set(isWellsVisibleAtom, !allVisible);
});

interface ScopeLayersSelectionProps {
  hideTitleAssets?: boolean;
  hideCRMTracts?: boolean;
}

export function ScopeLayersSelection({
  hideTitleAssets = false,
  hideCRMTracts = false,
}: ScopeLayersSelectionProps) {
  const [isCRMTractsVisible, setIsCRMTractsVisible] = useAtom(
    isCRMTractsVisibleAtom
  );
  const [isCRMSubtractsVisible, setIsCRMSubtractsVisible] = useAtom(
    isCRMSubtractsVisibleAtom
  );
  const [isTitleAssetsVisible, setIsTitleAssetsVisible] = useAtom(
    isTitleAssetsVisibleAtom
  );
  const [isWellsVisible, setIsWellsVisible] = useAtom(isWellsVisibleAtom);
  const [, toggleAllScopeLayers] = useAtom(toggleAllScopeLayersAtom);

  // Use React state instead of atom for isAllScopeLayersVisible
  const [isAllScopeLayersVisible, setIsAllScopeLayersVisible] = useState(true);

  // Update the state when any of the individual layers change
  useEffect(() => {
    // Only consider visible checkboxes for the "All" checkbox state
    const visibleLayers = [];

    if (!hideCRMTracts) visibleLayers.push(isCRMTractsVisible);
    visibleLayers.push(isCRMSubtractsVisible);
    if (!hideTitleAssets) visibleLayers.push(isTitleAssetsVisible);
    visibleLayers.push(isWellsVisible);

    setIsAllScopeLayersVisible(visibleLayers.every(Boolean));
  }, [
    isCRMTractsVisible,
    isCRMSubtractsVisible,
    isTitleAssetsVisible,
    isWellsVisible,
    hideCRMTracts,
    hideTitleAssets,
  ]);

  return (
    <>
      <div className={style.titleArea}>
        <div className={style.allCheckBox}>
          <Checkbox
            id="scopeAll"
            labelText="All"
            checked={isAllScopeLayersVisible}
            onChange={() => toggleAllScopeLayers()}
          />
        </div>
      </div>
      <div className={style.groupChecks}>
        {!hideCRMTracts && (
          <div className={style.crmTractsCheck}>
            <Checkbox
              id="scopeCRMTracts"
              labelText="CRM Tracts"
              checked={isCRMTractsVisible}
              onChange={() => setIsCRMTractsVisible(!isCRMTractsVisible)}
            />
          </div>
        )}
        <div className={style.crmSubtractsCheck}>
          <Checkbox
            id="scopeCRMSubtracts"
            labelText="CRM Subtracts"
            checked={isCRMSubtractsVisible}
            onChange={() => setIsCRMSubtractsVisible(!isCRMSubtractsVisible)}
          />
        </div>
        {!hideTitleAssets && (
          <div className={style.titleAssetsCheck}>
            <Checkbox
              id="scopeTitleAssets"
              labelText="Title Assets"
              checked={isTitleAssetsVisible}
              onChange={() => setIsTitleAssetsVisible(!isTitleAssetsVisible)}
            />
          </div>
        )}
        <div className={style.wellsCheck}>
          <Checkbox
            id="scopeWells"
            labelText="Wells"
            checked={isWellsVisible}
            onChange={() => setIsWellsVisible(!isWellsVisible)}
          />
        </div>
      </div>
    </>
  );
}
